<template>
	<div class="container-fluid p-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory"
				title="Back to stock on hand"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div>
				<h1 class="mb-0">Product Units</h1>
				<p class="text-muted mb-0">
					Each product has a base unit — weight, volume, or a count of discrete
					objects kept by each. Stock amounts are kept in it, which is what lets
					stock be summed, compared and reported.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div class="card">
			<div class="card-body">
				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div v-else-if="rows.length === 0" class="text-center py-4 text-muted">
					No products are in the shared catalog yet, so there is no unit to set.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Product</th>
								<th scope="col">
									Base unit
									<div class="text-muted small fw-normal">
										For example lb, kg, gal, L
									</div>
								</th>
								<th scope="col">
									Kind
									<div class="text-muted small fw-normal">
										What the unit measures
									</div>
								</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in sortedRows" :key="row.product_catalog_id">
								<td class="fw-semibold">{{ row.product_name }}</td>
								<td>
									<input
										v-model="drafts[row.product_catalog_id].unit"
										type="text"
										class="form-control form-control-sm"
										style="max-width: 10rem"
										placeholder="e.g. lb"
										:aria-label="`Base unit for ${row.product_name}`"
									/>
								</td>
								<td>
									<select
										v-model="drafts[row.product_catalog_id].kind"
										class="form-select form-select-sm"
										style="max-width: 10rem"
										:aria-label="`Unit kind for ${row.product_name}`"
									>
										<option
											v-for="option in KIND_OPTIONS"
											:key="option.value"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>
								</td>
								<td class="text-end text-nowrap">
									<span v-if="!isDirty(row)" class="text-muted small me-2">
										{{ row.unit === "" ? "Not set" : "Saved" }}
									</span>
									<ButtonLoading
										text="Save"
										icon="bi bi-check-lg"
										variant="outline-primary"
										size="sm"
										:loading="savingID === row.product_catalog_id"
										:disabled="!canSave(row)"
										@click="save(row)"
									/>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { InventoryUnit, InventoryUnitKind, UnitSet } from "@/type/api";

const KIND_OPTIONS: { value: InventoryUnitKind; label: string }[] = [
	{ value: "weight", label: "Weight" },
	{ value: "volume", label: "Volume" },
	{ value: "count", label: "Count (each)" },
];

const KIND_LABELS: Record<InventoryUnitKind, string> = {
	weight: "weight",
	volume: "volume",
	count: "count",
};

interface UnitDraft {
	unit: string;
	kind: InventoryUnitKind;
}

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const rows = ref<InventoryUnit[]>([]);
const loading = ref(false);
const error = ref("");
const savingID = ref<string | null>(null);

// An error is both the notification and the page state: the toast tells the
// operator, and the inline strip keeps the reason on screen after it dismisses.
function fail(title: string, e: any, fallback: string) {
	const message = e?.message || fallback;
	error.value = message;
	showNotification(title, message);
}

// One draft per product, seeded from the server, so an unsaved unit reads beside
// the stored one until Save replaces it.
const drafts = ref<Record<string, UnitDraft>>({});

const sortedRows = computed(() =>
	[...rows.value].sort((a, b) => a.product_name.localeCompare(b.product_name)),
);

function isDirty(row: InventoryUnit): boolean {
	const draft = drafts.value[row.product_catalog_id];
	if (!draft) return false;
	return draft.unit.trim() !== row.unit || draft.kind !== row.kind;
}

function canSave(row: InventoryUnit): boolean {
	const draft = drafts.value[row.product_catalog_id];
	return !!draft && draft.unit.trim() !== "" && isDirty(row);
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// The whole unit list through the generic QUERY endpoint (the legacy GET
		// list's replacement, doc/in-progress/query.md §11 Phase 4): every stated
		// unit is a draft row here, so the walk keeps the collection whole.
		const page = await apiClient.JSONQueryAll<InventoryUnit>(
			"/api/inventory/unit",
		);
		const loaded = page.items;
		rows.value = loaded;
		const next: Record<string, UnitDraft> = {};
		for (const row of loaded) {
			next[row.product_catalog_id] = { unit: row.unit, kind: row.kind };
		}
		drafts.value = next;
	} catch (e: any) {
		fail("Failed to load product units", e, "Please reload the page.");
	} finally {
		loading.value = false;
	}
}

async function save(row: InventoryUnit) {
	const draft = drafts.value[row.product_catalog_id];
	if (!draft) return;
	const unit = draft.unit.trim();
	if (unit === "" || !isDirty(row)) return;
	const body: UnitSet = { unit, kind: draft.kind };
	savingID.value = row.product_catalog_id;
	try {
		const updated = await apiClient.JSONPut<InventoryUnit>(
			`/api/inventory/unit/${encodeURIComponent(row.product_catalog_id)}`,
			body,
		);
		rows.value = rows.value.map((existing) =>
			existing.product_catalog_id === updated.product_catalog_id
				? updated
				: existing,
		);
		drafts.value[updated.product_catalog_id] = {
			unit: updated.unit,
			kind: updated.kind,
		};
		error.value = "";
		showNotification(
			"Unit saved",
			`${updated.product_name} is kept in ${updated.unit} (${
				KIND_LABELS[updated.kind] ?? updated.kind
			}).`,
		);
	} catch (e: any) {
		fail("Failed to save unit", e, "Please try again.");
	} finally {
		savingID.value = null;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
